import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { urlFor } from '@/lib/sanity/sanity';
import { estimateReadTime } from '@/components/utils/helper/estimateReadTime';
import { Blog } from '../type/type';
import CategoryLinks from './CategoryLinks';

interface BlogCardProps {
  blog: Blog;
  className?: string;
  isLatestBlog?: boolean;
}

const BlogCard: React.FC<BlogCardProps> = ({
  blog,
  className = '',
  isLatestBlog = false,
}) => {
  const {
    slug,
    title,
    excerpt,
    mainImage,
    author,
    categories,
    publishedAt,
    _createdAt,
    readTime,
    body,
  } = blog;

  const [copied, setCopied] = useState(false);

  // Use readTime from Sanity if available, otherwise calculate from body
  const displayReadTime = readTime || estimateReadTime(body);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const publishDate = formatDate(publishedAt || _createdAt);

  // Get primary category
  const primaryCategory =
    categories && categories.length > 0 ? categories : null;

  // Get active social media links from author, sorted by display order
  const activeSocialMedia = author?.socialMedia
    ?.filter((social) => social.isActive)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
    .slice(0, 4); // Show max 4 social icons

  // Handle copy URL functionality
  const handleCopyUrl = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const fullUrl = `https://optimitylogics.com/blog/${slug.current}`;

    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <Link
      key={blog._id}
      href={`/blog/${slug.current}`}
      className={`group relative flex h-full flex-col justify-between gap-5 rounded-[20px] border border-secGrayBeta bg-white p-3 shadow-blogCard transition-all duration-300 hover:-translate-y-1 hover:shadow-md md:p-5 ${className}`}
    >
      <div className={`flex flex-col gap-5 ${className} `}>
        <div className="w-full overflow-hidden rounded-xl">
          {mainImage ? (
            <Image
              src={urlFor(mainImage).url()}
              alt={title}
              width={800}
              height={600}
              loading="lazy"
              className={`h-auto w-full rounded-xl ${isLatestBlog ? 'aspect-[540/280]' : 'aspect-[351/175]'}`}
            />
          ) : (
            <div className="bg-gray-200 flex h-[300px] w-full items-center justify-center bg-whiteSmoke"></div>
          )}
        </div>

        {/* Blog Content */}
        <div className="flex flex-col gap-7">
          <div className="flex flex-col gap-3 font-opt">
            <div className="flex flex-col gap-2.5">
              {/* Category */}
              {primaryCategory && (
                <CategoryLinks categories={primaryCategory} />
              )}
              {/* Title */}
              <h3 className="line-clamp-2 text-base font-semibold leading-normal text-black md:text-lg">
                {title}
              </h3>
            </div>

            {/* Excerpt */}
            {excerpt && (
              <p className="line-clamp-2 text-sm font-normal leading-normal text-dgray md:text-base">
                {excerpt}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="border-t-2 border-t-lightBlue/20 bg-white pt-4 md:pointer-events-none md:absolute md:bottom-5 md:left-0 md:right-0 md:translate-y-8 md:px-5 md:opacity-0 md:transition-all md:duration-500 md:ease-in-out md:group-hover:pointer-events-auto md:group-hover:translate-y-0 md:group-hover:opacity-100">
        {/* Author and Social Media */}
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          {/* Author Details */}
          <div className="flex gap-3 md:flex-col">
            {/* Author Image */}
            <div className="bg-gray-200 relative h-10 w-10 overflow-hidden rounded-full">
              {author?.image ? (
                <Image
                  src={urlFor(author.image).url()}
                  alt={author.name}
                  width={40}
                  height={40}
                  className="object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-lightBlue text-lg font-bold text-white">
                  {author?.name?.charAt(0).toUpperCase() || 'A'}
                </div>
              )}
            </div>

            {/* Author Info */}
            <div className="flex flex-col gap-1 font-opt">
              <p className="text-base font-medium leading-normal text-blackAlpha">
                {author?.name || 'Anonymous'}
              </p>
              <span className="text-sm font-normal leading-normal text-blackNeturalAlpha">
                {publishDate} - {displayReadTime} min read
              </span>
            </div>
          </div>
          {activeSocialMedia && activeSocialMedia.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {activeSocialMedia.map((social, index) => {
                const isCopyButton =
                  social.platform.toLowerCase() === 'copy link';

                if (isCopyButton) {
                  return (
                    <button
                      key={index}
                      onClick={handleCopyUrl}
                      type="button"
                      className="bg-gray-200 relative overflow-hidden rounded-full transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-lightBlue"
                      title={copied ? 'Copied!' : 'Copy blog URL'}
                    >
                      {social.icon ? (
                        <Image
                          src={urlFor(social.icon).url()}
                          width={35}
                          height={35}
                          alt={copied ? 'Copied!' : 'Copy URL'}
                          className="object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-lightBlue text-xs font-bold text-white">
                          {copied ? '✓' : '🔗'}
                        </div>
                      )}
                      {copied && (
                        <div className="absolute inset-0 flex items-center justify-center bg-green-500 bg-opacity-80">
                          <span className="text-xs font-bold text-white">
                            ✓
                          </span>
                        </div>
                      )}
                    </button>
                  );
                }

                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-200 relative overflow-hidden rounded-full transition-transform duration-200 hover:scale-110"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {social.icon ? (
                      <Image
                        src={urlFor(social.icon).url()}
                        alt={social.platform}
                        width={35}
                        height={35}
                        className="object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-lightBlue text-xs font-bold text-white">
                        {social.platform.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
